import { forceCloseAdobeGenerateImageModule } from '@magicschool/business-logic/adobe';
import { broadcastNewToolChat } from '@magicschool/supabase/realtime';
import type { Enums, Tables } from '@magicschool/supabase/types';
import type { RealtimeChannel } from '@supabase/supabase-js';
import { createStoreSlice, type SetField } from 'features/store/zustand';
import type { AppRouterInstance } from 'next/dist/shared/lib/app-router-context.shared-runtime';
import { getFilterList } from '@/features/moderation/actions';
import type { InitialStateProps } from '@/features/store/store';
import { sanitizeUrl } from '@/util/sanitizeUrl';
import { revalidateCurrentRoom } from './actions';
import { getRoomStudent } from './rooms/[roomId]/actions';

type LoadProps = {
  room: Tables<'rooms'>;
  roomStudent: Tables<'room_students'>;
};

export type StudentStore = {
  loading: boolean;
  student: Tables<'students'> | undefined;
  user: Tables<'users'> | undefined;
  currentRoomStudent: (Tables<'room_students'> & { autoPaused?: boolean }) | null;
  currentRoom: Tables<'rooms'> | null;
  realtimeChannel: RealtimeChannel | null;
  flaggedKeywords: string[];
  pageTitleRef: React.RefObject<HTMLElement | null> | null;
  load: (data: LoadProps) => Promise<void>;
  onAssistantThreadMessage: (args: {
    threadId: number;
    tool_id: string;
    tool_uuid: string;
    message: string;
    message_created_at: string;
  }) => void;
  setField: SetField<StudentStore>;
  handleRoomStateEvent: (roomId: string, roomState: Enums<'room_state'>, router: AppRouterInstance, isLTI: boolean) => void;
  /** Navigates for a computed status (schedule or hours) without overwriting the teacher's state in `currentRoom`. */
  followRoomStatus: (roomId: string, status: Enums<'room_state'>, router: AppRouterInstance, isLTI: boolean) => void;
  handleRoomStudentStateEvent: (
    roomId: string,
    { roomStudentId, state }: { roomStudentId: string; state: Enums<'room_state'> },
    router: AppRouterInstance,
    isLTI: boolean,
  ) => void;
  createRoomStudentThread: (threadId: number, roomToolId?: string) => void;
};

const defaultState = {
  currentRoom: null,
  currentRoomStudent: null,
  loading: true,
  realtimeChannel: null,
  flaggedKeywords: [],
  pageTitleRef: null,
};

type InitialProps = Pick<InitialStateProps, 'studentData'>;

const saveUrlState = (roomId: string, isLTI: boolean) => {
  const lockedUrl = new URL(`${isLTI ? '/lti' : ''}/s/rooms/${roomId}/locked`, window.location.origin);
  lockedUrl.searchParams.set('next', window.location.href);
  lockedUrl.searchParams.set('magicStudentEnabled', 'true');

  return lockedUrl;
};

const handleRoomStateEvent = async (roomId: string, newState: Enums<'room_state'>, router: AppRouterInstance, isLTI: boolean) => {
  const searchParams = new URLSearchParams(window.location.search);
  const nextSearchParam = sanitizeUrl(searchParams.get('next'));
  if (newState === 'locked') {
    const lockedUrl = saveUrlState(roomId, isLTI);
    if (window.adobe) await forceCloseAdobeGenerateImageModule();
    router.replace(lockedUrl.href);
  } else if (newState === 'archived') {
    router.replace(`/s/rooms`);
  } else if (newState === 'paused') {
    // Stay on the current page; the tool pages render their own paused UI.
    return;
  } else {
    revalidateCurrentRoom(roomId); // ensures that we load the latest room / room student state when navigating from locked
    // Only leave the locked page; a student who stayed in a chat through a pause keeps their place on resume.
    if (window.location.pathname.endsWith('/locked')) {
      router.replace(nextSearchParam ?? `${isLTI ? '/lti' : ''}/s/rooms/${roomId}/tools`);
    }
  }
};

export const createStudentStoreSlice = ({ studentData }: InitialProps) =>
  createStoreSlice(
    'StudentStoreData',
    { ...defaultState, student: studentData?.student, user: studentData?.user },
    ({ set, get, getFull, setField }) => ({
      setField,
      load: async (data) => {
        const { data: keywords } = await getFilterList();
        set({ currentRoom: data.room, currentRoomStudent: data.roomStudent });
        set({ flaggedKeywords: keywords });
      },
      // This is being called (unintentionally) at least twice per message from StudentToolChatClient
      // This isn't causing any issues currently, but might in the future
      onAssistantThreadMessage: ({ tool_id, tool_uuid, message, message_created_at }) => {
        const { currentRoomStudent } = get();
        const room_tool_id = getFull().ChatStoreData.roomToolId;
        if (!currentRoomStudent || !room_tool_id) return;

        broadcastNewToolChat(currentRoomStudent.room_id, currentRoomStudent.id, {
          tool_id,
          tool_uuid,
          room_tool_id,
          message,
          message_created_at,
        });
      },
      handleRoomStateEvent: (roomId, roomState, router, isLTI) => {
        set((s) => (s.currentRoom ? { currentRoom: { ...s.currentRoom, state: roomState } } : {}));
        handleRoomStateEvent(roomId, roomState, router, isLTI);
      },
      followRoomStatus: (roomId, status, router, isLTI) => {
        handleRoomStateEvent(roomId, status, router, isLTI);
      },
      handleRoomStudentStateEvent: (roomId, { roomStudentId, state }, router, isLTI) => {
        if (get().currentRoomStudent?.id === roomStudentId) {
          // Who caused a pause is not on the room-wide broadcast (every classmate receives it); the
          // affected student reads it from their own auth-scoped row instead.
          set((s) => (s.currentRoomStudent ? { currentRoomStudent: { ...s.currentRoomStudent, state, autoPaused: false } } : {}));
          handleRoomStateEvent(roomId, state, router, isLTI);
          if (state === 'paused') {
            getRoomStudent()
              .then(({ data: fresh }) => {
                // Ignore a failed or stale answer if the student was resumed while it was in flight.
                if (fresh?.state !== 'paused' || get().currentRoomStudent?.state !== 'paused') return;
                set((s) => (s.currentRoomStudent ? { currentRoomStudent: { ...s.currentRoomStudent, autoPaused: fresh.autoPaused } } : {}));
              })
              .catch(() => {
                // Falls back to the manual-pause copy.
              });
          }
        }
      },
      createRoomStudentThread: (threadId: number, roomToolId?: string) => {
        fetch(`/api/s/room_students/threads`, {
          method: 'POST',
          body: JSON.stringify({ threadId, roomToolId }),
        });
        getFull().ChatStoreData.setField('roomToolId')(roomToolId ?? '');
      },
    }),
  );
